import React, { useState } from 'react';
import {
  Card,
  Button,
  Space,
  Tag,
  Typography,
  Descriptions,
  Spin,
  Modal,
  Form,
  Input,
  App,
  Alert,
} from 'antd';
import { ArrowLeftOutlined, CheckCircleOutlined, CloseCircleOutlined, PrinterOutlined } from '@ant-design/icons';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { qualityService, type InspectionRecord } from '@/services/quality.service';
import InspectionSamplesCard from '@/components/quality/InspectionSamplesCard';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { ENTITY_TYPE_RECEIVING_DIMENSIONAL_INSPECTION } from '@/constants/approval-entity-types';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

type DimensionalInspection = InspectionRecord & {
  lot_quantity?: number | null;
  sample_size?: number | null;
  sampling_plan?: string | null;
  approval_instance_id?: string | null;
  pending_result?: string | null;
  samples?: Array<{
    id?: string;
    receiving_inspection_detail_id?: string | null;
    sample_number?: number;
    sample_qty?: number;
    status?: string;
    notes?: string | null;
  }>;
  parent_receiving_inspection?: {
    id: string;
    inspection_number: string;
    status: string;
    result: string;
    source_document_id?: string | null;
  };
  grn?: {
    id: string;
    grn_number: string;
    grn_status: string;
    inspection_status: string;
  };
};

const ReceivingDimensionalInspectionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [completeOpen, setCompleteOpen] = useState(false);
  const [completeResult, setCompleteResult] = useState<'Pass' | 'Fail'>('Pass');
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['receiving-dimensional-inspection', id],
    queryFn: () => qualityService.getInspectionById(id!) as Promise<DimensionalInspection>,
    enabled: !!id,
  });

  const canComplete =
    data &&
    data.status !== 'Completed' &&
    data.status !== 'Cancelled' &&
    data.status !== 'Pending Approval' &&
    (data.result === 'Pending' || !data.result);

  const showApproval = Boolean(
    data?.approval_instance_id || data?.status === 'Pending Approval'
  );

  const openComplete = (result: 'Pass' | 'Fail') => {
    setCompleteResult(result);
    form.setFieldsValue({ findings: data?.findings || '' });
    setCompleteOpen(true);
  };

  const submitComplete = async () => {
    const values = await form.validateFields();
    if (completeResult === 'Fail' && !String(values.findings || '').trim()) {
      message.error('Findings are required when failing inspection');
      return;
    }
    setSaving(true);
    try {
      await qualityService.submitInspectionForApproval(id!, {
        result: completeResult,
        findings: values.findings,
      });
      message.success(
        completeResult === 'Pass'
          ? 'Dimension Pass submitted for approval'
          : 'Dimension Fail submitted for approval'
      );
      setCompleteOpen(false);
      await refetch();
      queryClient.invalidateQueries({ queryKey: ['inspections'] });
      queryClient.invalidateQueries({ queryKey: ['receiving-inspection'] });
    } catch (e: unknown) {
      message.error((e as Error)?.message || 'Failed to submit for approval');
    } finally {
      setSaving(false);
    }
  };

  if (isLoading || !data) {
    return <Spin style={{ display: 'block', margin: '48px auto' }} />;
  }

  const parent = data.parent_receiving_inspection;

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/qms/receiving-dimensional-inspections')}
        >
          Back to Dimension Inspections
        </Button>
        {id && (
          <Button
            icon={<PrinterOutlined />}
            onClick={() =>
              window.open(`/print/qms/receiving-dimensional-inspections/${id}`, '_blank')
            }
          >
            Print
          </Button>
        )}
      </Space>

      <Title level={3} style={{ marginTop: 0 }}>
        Receiving Dimension Inspection {data.inspection_number}
      </Title>
      <Text type="secondary">
        Simple Pass/Fail linked to Receiving Inspection — unlocks Receiving Pass when approved
        Passed
      </Text>

      {data.status === 'Pending Approval' && (
        <Alert
          style={{ marginTop: 16, marginBottom: 16 }}
          type="info"
          showIcon
          message="Pending approval"
          description={`Awaiting approval of ${data.pending_result || 'result'}.`}
        />
      )}

      {showApproval && id && (
        <Card title="Approval" size="small" style={{ marginTop: 16, marginBottom: 16 }}>
          <ApprovalPanel
            entityType={ENTITY_TYPE_RECEIVING_DIMENSIONAL_INSPECTION}
            entityId={id}
            extraInvalidateQueryKeys={[
              ['receiving-dimensional-inspection', id],
              ['inspections'],
              ['receiving-inspection'],
            ]}
          />
        </Card>
      )}

      {(parent || data.grn) && (
        <Alert
          style={{ marginTop: 16, marginBottom: 16 }}
          type="info"
          showIcon
          message={
            <Space wrap>
              {parent && (
                <span>
                  Receiving:{' '}
                  <Link to={`/scm/procurement/receiving-inspections/${parent.id}`}>
                    {parent.inspection_number}
                  </Link>
                </span>
              )}
              {data.grn && (
                <span>
                  GRN:{' '}
                  <Link to={`/scm/procurement/grn/${data.grn.id}`}>{data.grn.grn_number}</Link> (
                  {data.grn.grn_status})
                </span>
              )}
            </Space>
          }
        />
      )}

      <Card size="small">
        <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
          <Descriptions.Item label="Status">
            <Tag>{data.status}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Result">
            <Tag color={data.result === 'Pass' ? 'green' : data.result === 'Fail' ? 'red' : 'default'}>
              {data.result}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Category">
            {data.inspection_subcategory || data.inspection_category}
          </Descriptions.Item>
          <Descriptions.Item label="Scheduled">
            {data.scheduled_date ? dayjs(data.scheduled_date).format('DD/MM/YYYY') : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Actual">
            {data.actual_date ? dayjs(data.actual_date).format('DD/MM/YYYY') : '—'}
          </Descriptions.Item>
        </Descriptions>
        {data.observations && (
          <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            {data.observations}
          </Text>
        )}
        {data.findings && (
          <Text style={{ display: 'block', marginTop: 8 }}>
            <strong>Findings:</strong> {data.findings}
          </Text>
        )}
        {canComplete && (
          <Space style={{ marginTop: 16 }}>
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={() => openComplete('Pass')}
            >
              Pass — submit for approval
            </Button>
            <Button danger icon={<CloseCircleOutlined />} onClick={() => openComplete('Fail')}>
              Fail — submit for approval
            </Button>
          </Space>
        )}
      </Card>

      {id && (
        <InspectionSamplesCard
          inspectionId={id}
          canEdit={Boolean(canComplete)}
          lotQuantity={data.lot_quantity}
          sampleSize={data.sample_size}
          samplingPlan={data.sampling_plan}
          samples={data.samples}
          onSaved={async () => {
            await refetch();
          }}
        />
      )}

      <Modal
        title={
          completeResult === 'Pass'
            ? 'Submit Dimension Pass for approval'
            : 'Submit Dimension Fail for approval'
        }
        open={completeOpen}
        onCancel={() => setCompleteOpen(false)}
        onOk={() => void submitComplete()}
        confirmLoading={saving}
        okText="Submit for approval"
        okButtonProps={{ danger: completeResult === 'Fail' }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="findings"
            label="Findings / notes"
            rules={
              completeResult === 'Fail'
                ? [{ required: true, message: 'Required when failing' }]
                : undefined
            }
          >
            <TextArea rows={4} placeholder="Dimension inspection findings" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ReceivingDimensionalInspectionDetailPage;
